/**
 * Collection Page JavaScript
 * Infinite Scroll, Sorting, Performance Optimized, SEO Friendly
 */

(function() {
  'use strict';

  class CollectionPage {
    constructor() {
      // Use class selector instead of data attribute to be more specific
      this.section = document.querySelector('.collection-products[data-section-id]');
      if (!this.section) {
        console.warn('Collection page section not found');
        return;
      }

      this.grid = this.section.querySelector('[data-products-grid]');
      this.loader = this.section.querySelector('[data-products-loader]');
      this.sentinel = this.section.querySelector('[data-products-loader-sentinel]');
      this.endMessage = this.section.querySelector('[data-products-end]');
      this.sortSelect = this.section.querySelector('[data-sort-select]');
      
      if (!this.grid) return;
      
      this.collectionHandle = this.section.dataset.collectionHandle;
      this.productsPerPage = parseInt(this.section.dataset.productsPerPage) || 24;
      // Check for 'true' string or boolean true
      const infiniteScrollValue = this.section.dataset.infiniteScroll;
      this.enableInfiniteScroll = infiniteScrollValue === 'true' || infiniteScrollValue === true;
      this.scrollThreshold = parseInt(this.section.dataset.scrollThreshold) || 200;
      this.currentPage = parseInt(this.grid.dataset.currentPage) || 1;
      this.totalPages = parseInt(this.grid.dataset.totalPages) || 1;
      this.isLoading = false;
      // Only show hasMoreProducts if there are actually more pages to load
      // and we haven't loaded all products yet
      this.hasMoreProducts = this.currentPage < this.totalPages && this.totalPages > 1;
      this.currentSortBy = this.section.dataset.sortBy || 'manual';
      this.pagination = this.section.querySelector('[data-products-pagination]');
      
      this.init();
    }

    init() {
      // Debug logs
      console.log('CollectionPage initialized:', {
        enableInfiniteScroll: this.enableInfiniteScroll,
        hasMoreProducts: this.hasMoreProducts,
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        hasLoader: !!this.loader,
        hasSentinel: !!this.sentinel,
        hasPagination: !!this.pagination
      });

      // Initialize infinite scroll
      if (this.enableInfiniteScroll) {
        // Only show infinite scroll if there are more pages
        if (this.hasMoreProducts) {
          this.setupInfiniteScroll();
          
          // On mobile, check if we need to load more immediately
          // (if content is short and sentinel is already in view)
          if (window.innerWidth <= 749) {
            setTimeout(() => {
              this.checkMobileInitialLoad();
            }, 500);
          }
        } else {
          // If no more products, show end message immediately
          if (this.endMessage && this.totalPages > 0) {
            this.endMessage.style.display = 'block';
          }
        }
        // Hide pagination if infinite scroll is enabled
        if (this.pagination) {
          this.pagination.style.display = 'none';
        }
        // Hide end message initially if infinite scroll is enabled (will show when done)
        if (this.endMessage && this.hasMoreProducts) {
          this.endMessage.style.display = 'none';
        }
      } else {
        // Hide loader, sentinel and end message if pagination is enabled
        if (this.loader) {
          this.loader.style.display = 'none';
        }
        if (this.sentinel) {
          this.sentinel.style.display = 'none';
        }
        if (this.endMessage) {
          this.endMessage.style.display = 'none';
        }
      }

      // Initialize sorting
      if (this.sortSelect) {
        this.setupSorting();
      }

      // Mark initial items as loaded
      this.markItemsAsLoaded();
    }

    markItemsAsLoaded() {
      const items = this.grid.querySelectorAll('[data-product-item]');
      const isMobile = window.innerWidth <= 749;
      
      items.forEach((item, index) => {
        // On mobile, mark as loaded immediately for faster rendering
        if (isMobile) {
          item.setAttribute('data-loaded', 'true');
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        } else {
          setTimeout(() => {
            item.setAttribute('data-loaded', 'true');
          }, index * 50);
        }
      });
    }

    checkMobileInitialLoad() {
      // Check if sentinel is already visible on mobile (content is short)
      if (!this.hasMoreProducts || this.isLoading) return;
      
      const sentinel = this.sentinel;
      if (!sentinel) return;
      
      const rect = sentinel.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // If sentinel is already in or near viewport, trigger load
      if (rect.top <= windowHeight * 1.5) {
        console.log('Mobile: Sentinel already visible, triggering load');
        this.loadMoreProducts();
      }
    }

    setupInfiniteScroll() {
      // Use Intersection Observer for better performance
      if ('IntersectionObserver' in window) {
        this.setupIntersectionObserver();
      } else {
        // Fallback to scroll event
        this.setupScrollListener();
      }
    }

    setupIntersectionObserver() {
      // On mobile, use larger rootMargin for better detection
      const isMobile = window.innerWidth <= 749;
      const mobileMargin = isMobile ? Math.max(this.scrollThreshold * 2, 400) : this.scrollThreshold;
      
      const observerOptions = {
        root: null,
        rootMargin: `${mobileMargin}px`,
        threshold: 0.01 // Lower threshold for better mobile detection
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoading && this.hasMoreProducts) {
            console.log('Sentinel intersecting, loading more products');
            this.loadMoreProducts();
          }
        });
      }, observerOptions);

      // Use sentinel if available, otherwise use loader
      const targetElement = this.sentinel || this.loader;
      
      if (targetElement && this.hasMoreProducts) {
        // Make sure sentinel is visible and in the DOM
        if (this.sentinel) {
          this.sentinel.style.display = 'block';
          this.sentinel.style.visibility = 'visible';
          this.sentinel.style.opacity = '1';
          this.sentinel.style.transform = 'none';
          this.sentinel.style.height = isMobile ? '250px' : '100px';
          this.sentinel.style.minHeight = isMobile ? '250px' : '100px';
        }
        console.log('Setting up observer for:', targetElement, 'on mobile:', isMobile, 'margin:', mobileMargin);
        this.observer.observe(targetElement);
        
        // Also setup scroll listener as backup (always active for reliability)
        this.setupScrollListener();
      } else if (!this.hasMoreProducts) {
        // No more products, show end message
        if (this.endMessage) {
          this.endMessage.style.display = 'block';
        }
        console.log('No more products to load, infinite scroll disabled');
      } else {
        // Fallback to scroll listener
        console.warn('No target element found for infinite scroll, using scroll listener');
        this.setupScrollListener();
      }
    }

    setupScrollListener() {
      let ticking = false;
      let scrollListenerActive = true;

      // Don't duplicate scroll listener if already active
      if (this.scrollListenerAttached) {
        return;
      }
      this.scrollListenerAttached = true;

      const handleScroll = () => {
        if (ticking || !scrollListenerActive) return;
        ticking = true;

        requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          
          // On mobile, use larger threshold for earlier loading
          const isMobile = window.innerWidth <= 749;
          const threshold = isMobile ? Math.max(this.scrollThreshold * 2, 400) : this.scrollThreshold;
          
          const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

          if (distanceFromBottom <= threshold && 
              !this.isLoading && 
              this.hasMoreProducts) {
            scrollListenerActive = false; // Prevent multiple triggers
            console.log('Scroll threshold reached, loading more products', {
              distanceFromBottom,
              threshold,
              isMobile
            });
            // Load more products and re-enable listener after completion
            this.loadMoreProducts().then(() => {
              setTimeout(() => {
                scrollListenerActive = true;
              }, 500);
            }).catch(() => {
              setTimeout(() => {
                scrollListenerActive = true;
              }, 500);
            });
          }

          ticking = false;
        });
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      // Also trigger on initial load if content is short
      setTimeout(() => {
        handleScroll();
      }, 100);
    }

    async loadMoreProducts() {
      if (this.isLoading || !this.hasMoreProducts) return;

      this.isLoading = true;
      this.currentPage++;
      
      if (this.grid) {
        this.grid.setAttribute('data-loading', 'true');
      }

      if (this.loader) {
        this.loader.style.display = 'flex';
      }

      try {
        const url = this.buildProductsUrl();
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Try to find items in the new HTML
        let newItems = doc.querySelectorAll('[data-product-item]');
        let newGrid = doc.querySelector('[data-products-grid]');
        
        // If view=ajax template doesn't exist, try to find items in the full page
        if (newItems.length === 0) {
          // Try to find the collection-products section and extract items
          const section = doc.querySelector('.collection-products');
          if (section) {
            newItems = section.querySelectorAll('[data-product-item]');
            newGrid = section.querySelector('[data-products-grid]');
          }
        }
        
        // Update total pages if available
        if (newGrid && newGrid.dataset.totalPages) {
          this.totalPages = parseInt(newGrid.dataset.totalPages);
        }
        
        console.log('Loaded products:', {
          newItemsCount: newItems.length,
          currentPage: this.currentPage,
          totalPages: this.totalPages,
          url: url
        });

        if (newItems.length === 0 || this.currentPage >= this.totalPages) {
          // No more products to load
          this.hasMoreProducts = false;
          if (this.loader) {
            this.loader.style.display = 'none';
          }
          if (this.sentinel) {
            this.sentinel.style.display = 'none';
          }
          if (this.endMessage) {
            this.endMessage.style.display = 'block';
          }
          const targetElement = this.sentinel || this.loader;
          if (this.observer && targetElement) {
            this.observer.unobserve(targetElement);
          }
        } else {
          // Add new items with animation
          this.appendProducts(newItems);
          // Update hasMoreProducts based on remaining pages
          this.hasMoreProducts = this.currentPage < this.totalPages;
          
          // Ensure sentinel is visible after loading
          if (this.sentinel) {
            this.sentinel.style.display = 'block';
            this.sentinel.style.visibility = 'visible';
            // Re-observe sentinel after items are added (especially important on mobile)
            if (this.observer && this.hasMoreProducts) {
              setTimeout(() => {
                this.observer.unobserve(this.sentinel);
                this.observer.observe(this.sentinel);
                console.log('Re-observing sentinel after product load');
              }, 100);
            }
          }
          
          // Hide end message if we're still loading
          if (this.endMessage && this.hasMoreProducts) {
            this.endMessage.style.display = 'none';
          }
        }
      } catch (error) {
        console.error('Error loading more products:', error);
        this.currentPage--; // Revert page increment on error
        if (this.loader) {
          this.loader.style.display = 'none';
        }
      } finally {
        this.isLoading = false;
        if (this.grid) {
          this.grid.removeAttribute('data-loading');
        }
      }
    }

    buildProductsUrl() {
      const url = new URL(window.location.origin + '/collections/' + this.collectionHandle);
      url.searchParams.set('page', this.currentPage);
      url.searchParams.set('view', 'ajax');
      
      if (this.currentSortBy && this.currentSortBy !== 'manual') {
        url.searchParams.set('sort_by', this.currentSortBy);
      }

      return url.toString();
    }

    appendProducts(items) {
      if (!this.grid) return;

      const fragment = document.createDocumentFragment();
      let delay = 0;
      const isMobile = window.innerWidth <= 749;

      items.forEach((item, index) => {
        const clonedItem = item.cloneNode(true);
        
        // On mobile, show items immediately without animation
        if (isMobile) {
          clonedItem.style.opacity = '1';
          clonedItem.style.transform = 'translateY(0)';
          clonedItem.setAttribute('data-loaded', 'true');
        } else {
          clonedItem.style.opacity = '0';
          clonedItem.style.transform = 'translateY(20px)';
          clonedItem.removeAttribute('data-loaded');
          
          // Animate in
          setTimeout(() => {
            clonedItem.setAttribute('data-loaded', 'true');
            clonedItem.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
            requestAnimationFrame(() => {
              clonedItem.style.opacity = '1';
              clonedItem.style.transform = 'translateY(0)';
            });
          }, delay);
          
          delay += 50;
        }
        
        fragment.appendChild(clonedItem);
      });

      this.grid.appendChild(fragment);

      // On mobile, re-check sentinel position after animations start
      if (isMobile && this.sentinel && this.observer) {
        setTimeout(() => {
          // Force observer to re-check
          const rect = this.sentinel.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight * 1.5;
          if (isVisible && this.hasMoreProducts && !this.isLoading) {
            console.log('Mobile: Re-checking sentinel after product append');
            this.observer.unobserve(this.sentinel);
            this.observer.observe(this.sentinel);
          }
        }, 300);
      }

      // Update URL without page reload (for SEO)
      this.updateURL();
    }

    updateURL() {
      if (this.currentPage > 1) {
        const url = new URL(window.location);
        url.searchParams.set('page', this.currentPage);
        window.history.replaceState({}, '', url);
      }
    }

    setupSorting() {
      this.sortSelect.addEventListener('change', (e) => {
        const sortBy = e.target.value;
        this.currentSortBy = sortBy;
        
        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('sort_by', sortBy);
        url.searchParams.delete('page'); // Reset to first page
        
        // Reload page with new sort
        window.location.href = url.toString();
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CollectionPage();
    });
  } else {
    new CollectionPage();
  }
})();

